import Skeleton from "react-loading-skeleton";
import { getItem } from "../lib/utility";
import DarkAwareSkeletonTheme from "./DarkAwareSkeletonTheme";
import {
  BOOT_NAVBAR_ICON_COUNT,
  BOOT_SIDEBAR_LABEL_WIDTHS,
  BOOT_SIDEBAR_LINK_COUNT,
  readSidebarCollapsedPref,
} from "./bootSkeletonUtils";
import "./AppShellSkeleton.scss";

const AppShellSidebarSkeleton = ({ collapsed = false }) => (
  <aside className="app-shell-skeleton__sidebar">
    <div className="app-shell-skeleton__profile">
      <Skeleton width={42} height={42} borderRadius={10} />
      {!collapsed ? (
        <div className="app-shell-skeleton__profile-text">
          <Skeleton width={100} height={14} borderRadius={4} />
          <Skeleton width={70} height={10} borderRadius={4} />
        </div>
      ) : null}
    </div>

    <div className="app-shell-skeleton__links">
      {Array.from({ length: BOOT_SIDEBAR_LINK_COUNT }).map((_, index) => (
        <div key={index} className="app-shell-skeleton__link">
          <Skeleton width={22} height={22} borderRadius={4} />
          {!collapsed ? (
            <Skeleton
              width={
                BOOT_SIDEBAR_LABEL_WIDTHS[
                  index % BOOT_SIDEBAR_LABEL_WIDTHS.length
                ]
              }
              height={14}
              borderRadius={4}
            />
          ) : null}
        </div>
      ))}
    </div>

    <div className="app-shell-skeleton__logout">
      {collapsed ? (
        <Skeleton width={22} height={22} borderRadius={4} />
      ) : (
        <Skeleton width="100%" height={42} borderRadius={10} />
      )}
    </div>
  </aside>
);

const AppShellNavbarSkeleton = () => (
  <header className="app-shell-skeleton__navbar">
    <div className="app-shell-skeleton__navbar-actions">
      {Array.from({ length: BOOT_NAVBAR_ICON_COUNT }).map((_, index) => (
        <Skeleton
          key={`nav-icon-${index}`}
          className="app-shell-skeleton__nav-icon"
          width={40}
          height={40}
          borderRadius={8}
        />
      ))}
      <Skeleton
        className="app-shell-skeleton__nav-language"
        width={132}
        height={40}
        borderRadius={8}
      />
      <Skeleton
        className="app-shell-skeleton__nav-wallet"
        width={155}
        height={40}
        borderRadius={8}
      />
    </div>
  </header>
);

const AppShellPageSkeleton = () => (
  <div className="app-shell-skeleton__page">
    <Skeleton width={180} height={22} borderRadius={4} />
    <div className="app-shell-skeleton__cards">
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton key={index} height={88} borderRadius={12} />
      ))}
    </div>
    <Skeleton height={240} borderRadius={12} />
  </div>
);

/** Sidebar column only — used inside the real layout while it can't render yet. */
export const AppShellSidebarOnlySkeleton = ({
  collapsed = readSidebarCollapsedPref(getItem),
}) => (
  <DarkAwareSkeletonTheme>
    <div
      className={`app-shell-skeleton app-shell-skeleton--sidebar-only${
        collapsed ? " app-shell-skeleton--collapsed" : ""
      }`}
      aria-busy="true"
    >
      <AppShellSidebarSkeleton collapsed={collapsed} />
    </div>
  </DarkAwareSkeletonTheme>
);

/**
 * Full app chrome skeleton (sidebar + navbar + generic page).
 * Sidebar layout matches saved collapsed/expanded preference so boot
 * does not flash from full labels to icon-only.
 */
const AppShellSkeleton = ({
  showContentSkeleton = true,
  collapsed = readSidebarCollapsedPref(getItem),
}) => (
  <DarkAwareSkeletonTheme>
    <div
      className={`app-shell-skeleton${
        collapsed ? " app-shell-skeleton--collapsed" : ""
      }`}
      aria-busy="true"
      aria-label="Loading application"
    >
      <AppShellSidebarSkeleton collapsed={collapsed} />

      <div className="app-shell-skeleton__main">
        <AppShellNavbarSkeleton />
        <div className="app-shell-skeleton__contents">
          {showContentSkeleton ? <AppShellPageSkeleton /> : null}
        </div>
      </div>
    </div>
  </DarkAwareSkeletonTheme>
);

export default AppShellSkeleton;
